import * as React from 'react';
import { Fragment } from 'react';
import { Feature } from '@tencent/workbench';
import BaseColorFeature from '@framework/workbench/common/feature/base-color-feature';
import { config } from './config';
import {
    ColorPickerActionType,
    ColorPickerItemType,
    ColorPickerType,
    IItemList,
} from '@src/js/common/ark-extensions/dc-components/color-picker/type';
import Menu from '@tencent/dui/lib/components/Menu';
import i18n from '@i18next/i18n';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import LineStyleIcon from '@tencent/docs-design-resources/icon/desktop/svg/line_style_24.svg';
import LineThicknessIcon from '@tencent/docs-design-resources/icon/desktop/svg/line_thickness_outline_light_24.svg';
import ArrowIcon from '@tencent/docs-design-resources/icon/desktop/svg/arrow_type_outline_light_24.svg';
import { MoreConfigMenu } from '@framework/workbench/common/ui/more-config-menu';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
    TDWVer5Type,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { IBorderInfo } from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { IEndInfo } from '@src/slide/infrastructure/module/dependent/line-info/endStyle';
import { CustomSelect } from '@framework/workbench/common/components/custom-select';
import { defaultBorderColor } from '@src/slide/domain/ui-model/element-modeles/property/border';
import { FormItemName } from '@framework/workbench/drawer/constants';
import { BorderMenu, getBorderStyleIcon } from '@tencent/drawing-drawer-components/lib/components/border-menu';

const DEFAULT_BORDER_WIDTH = ptToCanvas(0.75);
const NO_FILL_BORDER = {
    color: null,
    width: 0,
    style: null,
    tdColor: null,
    noFill: 1,
};

const DEFAULT_BORDER: {
    style: any,
    headEnd?: IEndInfo | null,
    tailEnd?: IEndInfo | null,
} = {
    style: null,
    headEnd: null,
    tailEnd: null,
};

@Feature()
export class BorderColorFeature extends BaseColorFeature {
    id = config.id;
    config = config;
    cachedBorderStyle: {
        color?: string,
        tdColor?: TDColor,
        width?: number,
        style?: any,
        headEnd?: IEndInfo | null,
        tailEnd?: IEndInfo | null,
        noFill?: number,
    } = DEFAULT_BORDER;
    isPreview = false;
    public getColorDropDownContent = (menuFrom: string | number, options?: { hideExtendMenu: boolean }) => {
        const { value, color } = this.getModeAndValue();
        let itemList: IItemList = [];
        const selectedModel = this.getSelectionService().getSelectedModel();
        itemList.push({
            itemType: ColorPickerItemType.Menu,
            payload: {
                menus: <Fragment>
                    <Menu.SubMenu
                        onClick={() => false}
                        icon={LineThicknessIcon}
                        title={i18n.t('线条粗细')}
                        style={{
                            paddingLeft: 12,
                        }}
                    >
                        <BorderMenu
                            id={FormItemName.BORDER_LINE_WIDTH}
                            border={value}
                            hiddenCompBorder={true}
                            handleBorderMenuMouseEnter={this.handleBorderMenuMouseEnter}
                            handleBorderMenuMouseLeave={this.handleBorderMenuMouseLeave}
                            handleBorderMenuClick={(val) => {
                                this.handleBorderChange(val, false);
                            }}
                        />
                    </Menu.SubMenu>
                    <Menu.SubMenu
                        onClick={() => false}
                        icon={LineStyleIcon}
                        title={i18n.t('线条类型')}
                        style={{
                            paddingLeft: 12,
                        }}
                    >
                        <BorderMenu
                            id={ActionType.BORDER_LINE_STYLE}
                            border={value}
                            hiddenCompBorder={true}
                            handleBorderMenuMouseEnter={this.handleBorderMenuMouseEnter}
                            handleBorderMenuMouseLeave={this.handleBorderMenuMouseLeave}
                            handleBorderMenuClick={(val) => {
                                this.handleBorderChange(val, false);
                            }}
                        />
                    </Menu.SubMenu>
                </Fragment>,
            },
        });

        if (selectedModel?.type === UIModelType.CONNECTOR) {
            itemList.push({
                itemType: ColorPickerItemType.Menu,
                payload: {
                    menus: <Fragment>
                        <Menu.SubMenu
                            onClick={() => false}
                            icon={ArrowIcon}
                            title={i18n.t('箭头类型')}
                            style={{
                                paddingLeft: 12,
                            }}
                        >
                            <BorderMenu
                                id={ActionType.BORDER_ARROW_STYLE}
                                border={value}
                                hiddenCompBorder={true}
                                handleBorderMenuMouseEnter={this.handleBorderMenuMouseEnter}
                                handleBorderMenuMouseLeave={this.handleBorderMenuMouseLeave}
                                handleBorderMenuClick={this.handleBorderClick}
                            />
                        </Menu.SubMenu>
                    </Fragment>,
                },
            });
        }

        itemList.push({
            itemType: ColorPickerItemType.Menu,
            payload: {
                menus: <Fragment>
                    <MoreConfigMenu onClick={this.handleMoreConfigMenuClick} />
                </Fragment>,
            },
        });

        const actions: any = [];
        if (selectedModel?.type !== UIModelType.TABLE) {
            actions.push({
                text: i18n.t('无边框'),
                position: ColorPickerActionType.Top,
                onClick: this.handleNoColor,
            });
        }

        if (options?.hideExtendMenu) {
            itemList = [];
        }

        return this.getColorPickerDropDownContent({
            onColorChange: (tdColor, source) => {
                this.handleBorderColorChange(tdColor);
                this.getColorPickerManager()?.colorPickerTdwReport({
                    color: tdColor.srgb_color,
                    colorPickerFrom: menuFrom,
                    colorGroupFrom: source,
                    actionType: TDWVer5Type.COLOR_PICKER_BORDER,
                });
            },
            colorPickerType: ColorPickerType.StandardPC,
            selectedTdColor: color,
            onColorHover: this.onColorHover,
            onMouseLeave: this.onMouseLeave,
            onClick: this.onMouseLeave,
            actions,
            itemList,
        });
    };

    public getBorderMenu = (type) => {
        const { value } = this.getModeAndValue();
        return <BorderMenu
            id={type}
            border={value}
            hiddenCompBorder={false}
            handleBorderMenuMouseEnter={this.handleBorderMenuMouseEnter}
            handleBorderMenuMouseLeave={this.handleBorderMenuMouseLeave}
            handleBorderMenuClick={(value) => {
                this.handleBorderClick(value);
                this.reportBorder(type);
            }}
        />;
    };

    public reportBorder = (type) => {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_STYLE_TAB,
            action: type === ActionType.BORDER_LINE_STYLE
                ? TDWActionType.TABLE_TAB_STYLE_BORDER_TYPE
                : TDWActionType.TABLE_TAB_STYLE_BORDER_WEIGHT,
        });
    };

    public getBorderStyleToolbarButton = () => {
        const { value } = this.getModeAndValue();
        const styleId = value?.style;
        const icon = getBorderStyleIcon(styleId);

        return <CustomSelect>
            {
                <img src={icon} style={{
                    width: '100%',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                }} />
            }
        </CustomSelect>;
    };

    public getBorderWidthToolbarButton = () => {
        const { value } = this.getModeAndValue();
        const height = (value?.width || 1.5) / 2;

        return <CustomSelect>
            {
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    height: 12,
                }}>
                    <div style={{
                        flexShrink: 0,
                        marginRight: 6,
                        color: 'rgba(0, 0, 0, 0.88)',
                        fontSize: 12,
                        height: 12,
                        lineHeight: '12px',
                    }}>
                        { `${height}${i18n.t('磅')}` }
                    </div>
                    <div style={{
                        height,
                        width: '100%',
                        background: '#5c5c5c',
                        flexShrink: 1,
                    }}
                    />
                </div>
            }
        </CustomSelect>;
    };

    /**
     * 清理缓存
     * @param isPreview
     */
    private clearCached = (isPreview: boolean) => {
        if (!isPreview) {
            this.cachedBorderStyle = DEFAULT_BORDER;
            this.isPreview = false;
        }
    };

    /**
     * 点击无边框
     */
    private handleNoColor = () => {
        this.clearCached(false);
        this.saveRecentColorWithId(null);
        this.handleBorderNoFill(false);
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
        // 上报
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_EDIT_FORMAT,
            action: TDWActionType.CLK_NOFRAME,
        });
    };

    /**
     * 边框颜色设置
     * @param tdColor
     */
    private handleBorderColorChange = (tdColor: ITDColor) => {
        const selectedModel = this.getSelectionService().getSelectedModel();
        this.saveRecentColorWithId(tdColor);
        let props: IBorderInfo = { color: tdColor.srgb_color, tdColor };
        const { value } = this.getModeAndValue();
        if (selectedModel?.type !== UIModelType.TABLE) {
            const width = value?.width ? value.width : DEFAULT_BORDER_WIDTH;
            props = { color: tdColor.srgb_color, width, tdColor };
        }
        if (selectedModel?.type === UIModelType.TABLE) {
            props = { color: tdColor.srgb_color, tdColor };
        }
        this.handleBorderChange(props, false);
        if (this.checkSelectType() <= 1) {
            this.getAppService()?.editableServiceLayer?.previewService.updateElementPreviewState(false);
        }
    };

    /**
     * 表格边框处理
     * @param border
     * @param isPreview
     */
    private handleTableBorderChange = (border: IBorderInfo, isPreview: boolean) => {
        const selectedModel = this.getSelectionService().getSelectedModel() as (IElementBaseModel | IMultiplyModel);
        const borderInfo = border;
        selectedModel?.getBorder?.()?.updateCurrentBorder(borderInfo, isPreview, isPreview);
        this.clearCached(isPreview);
    };

    /**
     * 颜色预览
     * @param color
     */
    private onColorHover = (color) => {
        if (this.checkSelectType() > 1) {
            return;
        }
        const { value } = this.getModeAndValue();
        this.setCachedStyle(value);
        const isPreview = this.getAppService().editableServiceLayer?.previewService?.isPreview;
        if (!isPreview) {
            this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(true);
        }
        const selectedModel = this.getSelectionService().getSelectedModel() as (IElementBaseModel);
        selectedModel?.getBorder?.()?.updateCurrentBorder({ color }, true, true);
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(true);
    };

    /**
     * 颜色还原
     */
    private onMouseLeave = () => {
        const isPreview = this.getAppService().editableServiceLayer?.previewService?.isPreview;
        const borderInfo: IBorderInfo = {};
        Object.assign(borderInfo, this.cachedBorderStyle);
        this.clearCached(!!isPreview);
        if (this.checkSelectType() > 1 || !isPreview) {
            return;
        }

        if (borderInfo.noFill === 1) {
            this.handleBorderNoFill(true);
        } else {
            this.handleBorderChange(borderInfo, true);
        }
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
    };

    /**
     * style/width还原
     */
    private handleBorderMenuMouseLeave = () => {
        const isPreview = this.getAppService().editableServiceLayer?.previewService?.isPreview;
        const borderInfo: IBorderInfo = {};
        Object.assign(borderInfo, this.cachedBorderStyle);
        this.clearCached(!!isPreview);
        if (this.checkSelectType() > 1 || !isPreview) {
            return;
        }

        if (borderInfo.noFill === 1) {
            this.handleBorderNoFill(true);
        } else {
            this.handleBorderChange(borderInfo, true);
        }
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
    };

    /**
     * style/width预览
     */
    private handleBorderMenuMouseEnter = (previewValue) => {
        if (this.checkSelectType() > 1) {
            return;
        }
        const { value } = this.getModeAndValue();
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(true);
        this.setCachedStyle(value);
        this.handleBorderChange(previewValue, true);
    };

    /**
     * 缓存原始属性，预览时会临时修改边框数据，通过缓存恢复
     * @param value
     * @private
     */
    private setCachedStyle(value) {
        if (this.isPreview) return;

        if (!this.cachedBorderStyle?.noFill && this.isNoFill(value)) {
            this.cachedBorderStyle.noFill = 1;
        } else {
            Object.assign(this.cachedBorderStyle, value);
        }

        this.isPreview = true;
    }

    private isNoFill = (value: IBorderInfo) => value.noFill === 1 || (!value.tdColor || !value.width);

    /**
     * 设置无边框
     */
    private handleBorderNoFill = (isPreview: boolean) => {
        const selectedModel = this.getSelectionService().getSelectedModel() as (IElementBaseModel | IMultiplyModel);
        if (selectedModel.type === UIModelType.TABLE) {
            this.handleTableBorderChange(NO_FILL_BORDER, isPreview);
            return;
        }

        selectedModel?.getBorder?.()?.updateCurrentBorder(NO_FILL_BORDER, isPreview, isPreview);
    };

    /**
     * 设置无边框以外的属性
     * @param border
     * @param isPreview
     */
    private handleBorderChange = (border: IBorderInfo, isPreview: boolean) => {
        const selectedModel = this.getSelectionService().getSelectedModel() as (IElementBaseModel | IMultiplyModel);
        const modelType = selectedModel.type;

        // 设置属性，合并原始属性和新增的属性
        const mergeBorder: IBorderInfo = Object.assign(
            {},
            modelType === UIModelType.MULTIPLY ? {} : this.cachedBorderStyle,
            border,
        );
        this.clearCached(isPreview);
        if (modelType === UIModelType.TABLE) {
            this.handleTableBorderChange(border, isPreview);
            return;
        }

        // 空填充的边框，在设置粗细/样式时，给一个默认色值
        if (mergeBorder.noFill === 1 || !mergeBorder.tdColor) {
            mergeBorder.noFill = undefined;
            mergeBorder.tdColor = border.tdColor || new TDColor({
                rgbaStr: border.color || defaultBorderColor,
            });

            mergeBorder.color = border.color || mergeBorder.tdColor.srgbColor;
        }

        selectedModel?.getBorder?.()?.updateCurrentBorder(mergeBorder, isPreview, isPreview);
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(isPreview);
    };

    /**
     * 边框菜单点击
     * @param value
     */
    private handleBorderClick = (value) => {
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
        this.handleBorderChange(value, false);
    };
}
